import { ZodString } from 'zod';

import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

/**
 * NOTE: THIS IS JUST AN ADDITIONAL LAYER FOR EMAIL VALIDATION.
 * It is not a replacement for `z.string.email` or any other email validation functionality.
 * It's just a secondary check which tests if spec-compliant emails match our requirements.
 */
export function isValidEmail(rawEmail: string) {
  /**
   * Email addresses are case insensitive, it's better cast to lowercase
   */
  const email = rawEmail.toLocaleLowerCase();

  /**
   * We expect email to contain "local part" and "domain" and to look like "local-part@domain.tld".
   * If enquoted - multiple @ are allowed (e.g. '"user@something"@example.com') in "local part", we don't want it.
   */
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }

  const [username, domain] = parts;

  /**
   * Joi follows RFC 3696, but this is pretty dangerous.
   * In practice, most mailing servers will fail to receive "valid-only-on-paper" emails.
   * We should only allow:
   * - dot (.)
   * - underscore (_) (only for username)
   * - plus (+) (only for username)
   * - hyphen (-)
   * - lowercase latin letters a to z and digits from 0 to 9
   */
  return /^[a-z0-9+_\-.]+$/.test(username) && /^[a-z0-9\-.]+$/.test(domain);
}

export const ZodSecureEmail = ZodTrimmedString.toLowerCase()
  .email()
  .refine((email) => isValidEmail(email), { message: 'invalid email format' });

export const ZodExposedSecureEmail = ZodTrimmedString.toLowerCase()
  .email()
  .refine((email) => isValidEmail(email), { message: 'invalid email format' });

const buildZodSecureEmail = <Type extends ZodString>(type: Type) =>
  type
    .toLowerCase()
    .email()
    .refine((email) => isValidEmail(email), {
      message: 'invalid email format',
    });

export const ZodAppUserEmail = buildZodSecureEmail(ZodTrimmedString.max(60));
